import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BrowserStorageService } from '@app/services/browser-storage.service';
import { EncryptionService } from '@app/services/encryption.service';
import { AvailableAmountService } from '@app/shared/services/available-amount.service';
import { UserRoleService } from '@app/core/auth/user-role.service';
import { BalanceEntity } from '@app/domain/models/balance-entity.model';
import {
  SPEI_PROVIDER_SELECTION_ROUTE,
  speiProviderChoiceNeeded,
} from '@app/domain/models/product-selection.model';

/**
 * Marca de "la sesión YA eligió contexto SPEI". La escriben quienes fijan la
 * cuenta padre por elección (account-selection, el acordeón de `/products`) y
 * este servicio cuando no había nada que elegir. El login la BORRA, así que
 * entrar por Cards no la deja puesta.
 */
const SPEI_CHOSEN_KEY = 'selectedCompanyId';

@Injectable({ providedIn: 'root' })
export class ProviderContextService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);
  private readonly browserStorage = inject(BrowserStorageService);
  private readonly encryption = inject(EncryptionService);
  private readonly availableAmountService = inject(AvailableAmountService);
  private readonly userRole = inject(UserRoleService);
  private routerSub?: Subscription;

  private lastBalanceRefresh = 0;
  private readonly BALANCE_REFRESH_THROTTLE_MS = 2000;

  /** Inicia la escucha de navegación para asegurar provider y refrescar saldo. */
  init(): void {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return;
    if (!sessionStorage.getItem('access')) return;

    this.ensureProviderForCurrentRoute();
    this.refreshBalanceOnNavigate();

    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.ensureProviderForCurrentRoute();
      this.refreshBalanceOnNavigate();
    });
  }

  /** Refresca el saldo desde api/father_account/balances (o endpoint según entidad) al cambiar de página. */
  private refreshBalanceOnNavigate(): void {
    const now = Date.now();
    if (now - this.lastBalanceRefresh < this.BALANCE_REFRESH_THROTTLE_MS) return;
    this.lastBalanceRefresh = now;
    this.availableAmountService.refreshBalance();
  }

  destroy(): void {
    this.routerSub?.unsubscribe();
  }

  /**
   * Ya no forzamos switch de provider. La selección del usuario en el carrusel
   * (Cta_Padre en posición N del array father_accounts_list) es la fuente de verdad.
   * Solo se navega entre entidades (empresas, UN, cuentas) de ESE Cta_Padre.
   */
  private ensureProviderForCurrentRoute(): void {
    /* Sin switch automático: respetamos la selección del carrusel */
  }
  /**
   * Entrada a una pantalla SPEI (la regla ÚNICA: la usan `speiProviderGuard`
   * —menú Suite y URL directa— y el clic en el servicio Suite del sidebar).
   *
   * - Si la sesión ya eligió contexto SPEI ⇒ `null` (adelante, sin tocar nada:
   *   se respeta el nivel en el que va el usuario; cambiarlo es del company-selector).
   * - Si no, y hay algo que elegir (2+ rieles, o un riel con 2+ cuentas padre;
   *   `speiProviderChoiceNeeded`) ⇒ `UrlTree` al selector de cuenta padre con
   *   `returnUrl` = la pantalla pedida, para aterrizar ahí al elegir.
   * - Si no hay nada que elegir ⇒ se fija ese único contexto y `null`.
   *
   * Antes no se preguntaba: el login deja en `balance_object` TODAS las cuentas
   * padre y el sidebar tomaba la primera (`[0]`) como "contexto válido", así que
   * quien entraba por Cards aterrizaba en SPEI con un proveedor asumido.
   */
  speiEntryRedirect(requestedUrl: string): UrlTree | null {
    if (this.speiChosenInSession()) return null;
    const inventario = this.readInventory();
    if (speiProviderChoiceNeeded(inventario, this.userRole.getSpeiProviders())) {
      return this.router.createUrlTree([SPEI_PROVIDER_SELECTION_ROUTE], {
        queryParams: { returnUrl: requestedUrl },
      });
    }
    this.ensureSpeiProviderActive();
    return null;
  }

  /** ¿La sesión ya fijó contexto SPEI (por elección o por no haber nada que elegir)? */
  speiChosenInSession(): boolean {
    return !!this.browserStorage.getSessionStorageItem(SPEI_CHOSEN_KEY);
  }

  /**
   * Garantiza que haya un contexto SPEI válido, y NADA más.
   *
   * "Garantiza" es literal: si el contexto activo ya es de un proveedor que el
   * usuario tiene asignado, no se toca (solo se deja la marca). Reactivar la
   * cuenta padre tiraría la navegación de quien va en empresa → unidad → cuenta.
   *
   * Solo se activa una cuenta padre cuando NO hay contexto utilizable. Nunca se
   * activan proveedores ajenos: `father_accounts_list` trae TODOS los padres del
   * sistema, no solo los del usuario. Con 2+ cuentas padre que elegir NO se debe
   * llegar aquí sin elección: eso lo filtra `speiEntryRedirect`.
   */
  ensureSpeiProviderActive(): void {
    const allowed = this.userRole.getSpeiProviders();
    const providerOf = (f: BalanceEntity | null | undefined): string => String(f?.provider ?? '').trim();
    const isAllowed = (p: string): boolean => !allowed.length || allowed.includes(p);

    // 1) Contexto ya válido: entidad activa que conservar Y de un proveedor del usuario.
    const activa = this.readEntities('balance_object')[0] ?? null;
    const activeProvider = this.readActiveProviderName();
    if (activa?.id_entity_name && activeProvider && isAllowed(activeProvider)) {
      this.markChosen();
      return;
    }

    // 2) Sin contexto utilizable: primera cuenta padre de un proveedor asignado.
    const father = this.readEntities('father_accounts_list').find((f) => isAllowed(providerOf(f))) ?? null;
    if (father) this.activateFatherAccount(father);
  }

  /**
   * Escribe la marca "la sesión eligió contexto SPEI" con la cuenta padre dada,
   * en el formato ÚNICO que lee `speiEntryRedirect`: su `polipay_account` o, sin
   * él, su CLABE. La usan account-selection (al elegir tarjeta) y este servicio;
   * sin identificador no se escribe nada (una marca vacía no prueba elección).
   */
  markSpeiChosen(father: Pick<BalanceEntity, 'polipay_account' | 'clabe'> | null | undefined): void {
    const id = String(father?.polipay_account || father?.clabe || '').trim();
    if (id) sessionStorage.setItem(SPEI_CHOSEN_KEY, id);
  }

  /**
   * Elegir entidad en el `company-selector` de la barra lateral también es elegir
   * contexto SPEI: se marca con la cuenta PADRE del contexto recién fijado (la
   * misma que resuelve `AvailableAmountService.getActiveFatherAccount`), para que
   * la siguiente pantalla SPEI no vuelva a mandar al selector.
   */
  markActiveSpeiContextChosen(): void {
    this.markSpeiChosen(this.availableAmountService.getActiveFatherAccount());
  }

  /** Deja la marca con la cuenta PADRE del contexto, sin tocar el nivel activo. */
  private markChosen(): void {
    if (this.speiChosenInSession()) return;
    this.markActiveSpeiContextChosen();
  }

  /** Fija la cuenta padre activa y refresca su saldo (mismo mecanismo que account-selection). */
  private activateFatherAccount(father: BalanceEntity): void {
    const serialized = this.encryption.encrypt(JSON.stringify([father]));
    this.browserStorage.setSessionStorageItem('balance_object', serialized);
    this.browserStorage.setSessionStorageItem('selectedFatherAccount', serialized);
    const polipay = father.polipay_account || father.clabe;
    this.markSpeiChosen(father);
    this.availableAmountService.setBalanceInfo(father);
    if (polipay) {
      this.availableAmountService.getCurrentBalance(polipay).subscribe({ error: () => {} });
    }
  }

  /** Proveedor del contexto activo (selectedFatherAccount, respaldo balance_object). */
  private readActiveProviderName(): string {
    for (const key of ['selectedFatherAccount', 'balance_object']) {
      const provider = String(this.readEntities(key)[0]?.provider ?? '').trim();
      if (provider) return provider;
    }
    return '';
  }

  /**
   * Inventario COMPLETO de entidades (mismo criterio que `/products`): no se lee
   * `balance_object` como fuente principal porque una elección lo reescribe con
   * una sola cuenta padre.
   */
  private readInventory(): BalanceEntity[] {
    const inventario = [...this.readEntities('father_accounts_list'), ...this.readEntities('extra_gestor_entities')];
    return inventario.length > 0 ? inventario : this.readEntities('balance_object');
  }

  private readEntities(key: string): BalanceEntity[] {
    const cifrado = this.browserStorage.getSessionStorageItem(key);
    if (!cifrado) return [];
    try {
      const parsed = JSON.parse(this.encryption.decrypt(cifrado) || '[]');
      return (Array.isArray(parsed) ? parsed : [parsed]).filter(Boolean);
    } catch {
      return [];
    }
  }
}
